import * as Sentry from '@sentry/vue'
import SurveyMutations from '@/vuex/mutations/survey'
import SurveyGetters from '@/vuex/getters/survey'
import store from '@/vuex/store'
import SurveyApi from '@/api/survey'
import PresentationSessionApi from '@/api/presentationSession'

let uniqueSurveyId = null
const { isBackstage } = store.state
const PRESENTING_EXPERIENCE_SURVEY_CODE = 'PRESENTING'
const PRESEXPREVIEW_SURVEY_CODE = 'PRESEXPREVIEW'
// Survey codes that should trigger survey for all sessions of user (only display survey once in a user's session)
const SURVEY_CODES_TRIGGER_ALL_SESSIONS = [
  PRESENTING_EXPERIENCE_SURVEY_CODE,
  PRESEXPREVIEW_SURVEY_CODE,
]
const DELAY_TIME = 500

const initActiveSurveyData = async () => {
  try {
    const res = await SurveyApi.getActiveSurveysForPresenter()
    if (!res) return
    SurveyMutations.setActiveSurveys(store.state, res.surveys)
    if (window.CE2) {
      if (window.CE2.identify) {
        window.CE2.identify(store.state.currentUser.id)
      }
    } else {
      window.CE_READY = () => {
        if (window.CE2) {
          if (window.CE2.identify) {
            window.CE2.identify(store.state.currentUser.id)
          }
        }
      }
    }
  } catch (error) {
    Sentry.captureException(error)
  }
}

const isWildcardMatch = (pattern, path) => {
  const patternParts = pattern.split('/')
  const pathParts = path.split('/')
  let result = false
  for (let i = 0; i < patternParts.length; i++) {
    if (patternParts[i] === '*' && patternParts[i - 1] == pathParts[i - 1]) {
      result = true
      break
    }
    if (patternParts[i] !== pathParts[i]) {
      result = false
    }
  }

  // Try regex match as fallback
  if (!result) {
    try {
      const pathMatches = path.match(new RegExp(pattern, 'g'))
      if (pathMatches) {
        result = true
      }
    } catch (error) {
      // Ignore regex errors
    }
  }

  return result
}

const matchesTargetPath = (survey, path) => {
  const hasTargetPagesArray = survey.targetPages && Array.isArray(survey.targetPages)
  if (!hasTargetPagesArray) return false

  return survey.targetPages.some((targetPage) => targetPage === path || isWildcardMatch(targetPage, path))
}

const getSurveysForPath = (path) => {
  const surveys = SurveyGetters.getActiveSurveys(store.state) || []
  return surveys.filter((survey) => matchesTargetPath(survey, path))
}

const delay = (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

const removeSurveyScriptIfExisted = async (surveyId) => {
  if (!surveyId) {
    surveyId = uniqueSurveyId
  }
  await delay(DELAY_TIME)
  const scriptId = `survey-script-${surveyId}`
  const existingScript = document.getElementById(scriptId)
  if (!existingScript) return
  existingScript.remove()
}

const addSurveyTriggerScript = async (surveyId, delayTime) => {
  if (!window.CE2) return
  await delay(delayTime)
  const surveyScript = document.createElement('script')
  surveyScript.type = 'text/javascript'
  surveyScript.id = `survey-script-${surveyId}`
  surveyScript.innerHTML = `
    (window.CE_API || (window.CE_API=[])).push(function(){
        CE2.showSurvey("${surveyId}");
    });
  `
  document.head.appendChild(surveyScript)
}

const handleTriggerScript = async (surveyId, survey) => {
  await removeSurveyScriptIfExisted(surveyId)
  const delayTime = ((survey.delayTime && survey.delayTime > 0) ? survey.delayTime : 0) * 1000
  addSurveyTriggerScript(surveyId, delayTime)
  if (SURVEY_CODES_TRIGGER_ALL_SESSIONS.includes(survey.surveyCode)) {
    PresentationSessionApi.triggerSurveyForAllSessionOfUser() // only display survey once in a user's session
  }
}

const getSurveyIdAndTriggerScript = async (survey) => {
  const res = await SurveyApi.getSurvey(survey.surveyCode)
  if (res.surveyId && !isBackstage) {
    handleTriggerScript(res.surveyId, survey)
    return res.surveyId // Returning surveyId if it's handled
  }
  return null
}

const checkToShowSurveyOnAPage = async (path) => {
  if (!window.CE2) return
  try {
    const activeSurveysOnCurrentPage = getSurveysForPath(path)
    if (!activeSurveysOnCurrentPage) return
    for (let i = 0; i < activeSurveysOnCurrentPage.length; i++) {
      const survey = activeSurveysOnCurrentPage[i]
      // eslint-disable-next-line no-await-in-loop
      const surveyId = await getSurveyIdAndTriggerScript(survey)

      if (surveyId) {
        uniqueSurveyId = surveyId
        break
      }
    }
  } catch (error) {
    Sentry.captureException(error)
  }
}

const addStyleDisplayNoneToSurvey = async () => {
  if (SurveyGetters.isEURegion) return
  await delay(DELAY_TIME)
  const surveyElements = document.querySelectorAll('crazyegg-survey')
  if (!surveyElements) return
  surveyElements.forEach((surveyElement) => {
    surveyElement.style.display = 'none'
  })
}

const removeStyleDisplayNoneToSurvey = async () => {
  if (SurveyGetters.isEURegion) return
  await delay(DELAY_TIME)
  const surveyElements = document.querySelectorAll('crazyegg-survey')
  if (!surveyElements) return
  surveyElements.forEach((surveyElement) => {
    surveyElement.style.display = ''
  })
}

const alreadyHasSurveyScript = () => {
  const surveyScript = document.querySelectorAll('survey--init-script')
  if (!surveyScript || !surveyScript.length) return false
  return true
}

const getSurveyAndCheckVisibilityBySurveyCode = async (surveyCode) => {
  const surveys = SurveyGetters.getActiveSurveys(store.state) || []
  const matchSurveys = surveys.filter((survey) => survey.surveyCode === surveyCode)
  if (!matchSurveys || !matchSurveys.length) {
    return null
  }
  const survey = matchSurveys[0]
  const surveyId = await getSurveyIdAndTriggerScript(survey)
  if (surveyId) {
    uniqueSurveyId = surveyId
  }
  return surveyId
}

export default {
  initActiveSurveyData,
  checkToShowSurveyOnAPage,
  addSurveyTriggerScript,
  removeSurveyScriptIfExisted,
  addStyleDisplayNoneToSurvey,
  removeStyleDisplayNoneToSurvey,
  alreadyHasSurveyScript,
  getSurveyAndCheckVisibilityBySurveyCode,
}
